import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import ProjectCard from "../components/ProjectCard";
import { STAGES, stageOf } from "../components/ProjectStage";
import { usePageMeta } from "../lib/seo";

const TABS = [["all", "All projects"], ["verified_fundraising", "Verified — fundraising"], ["under_verification", "Under verification"], ["funded_implementation", "Funded — implementation"], ["completed_monitoring", "Completed — monitoring"]];
const LEGACY = { fundraising: "verified_fundraising", in_progress: "funded_implementation", completed: "completed_monitoring" };

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [sp, setSp] = useSearchParams();
  const raw = sp.get("stage") || sp.get("status") || "all";
  const tab = TABS.some(([k]) => k === raw) ? raw : LEGACY[raw] || "all";
  usePageMeta("Projects", "Every Dutsen Kauna Foundation project with its verification stage: under verification, verified and fundraising, funded and in implementation, or completed and monitored.");
  useEffect(() => { api.get("/projects").then((r) => setProjects(r.data)).catch(() => {}); }, []);
  const counts = useMemo(() => projects.reduce((a, p) => ({ ...a, [stageOf(p)]: (a[stageOf(p)] || 0) + 1 }), {}), [projects]);
  const shown = tab === "all" ? projects : projects.filter((p) => stageOf(p) === tab);
  return (
    <div data-testid="projects-page">
      <section className="relative bg-[#155e3f] text-white py-24 overflow-hidden">
        <img src="https://images.unsplash.com/photo-1617056239820-8ce90ba48193?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80" alt="" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#155e3f] via-[#155e3f]/85 to-[#155e3f]/40" />
        <div className="dk-container relative">
          <span className="text-[#d6aa43] tracking-[0.3em] text-xs font-semibold uppercase">Projects &amp; impact</span>
          <h1 className="font-serif text-4xl md:text-6xl font-bold mt-3 max-w-3xl">Know the stage before you give.</h1>
          <p className="mt-5 text-white max-w-2xl">Every project moves through four public stages. Project-specific donations are accepted only once a need is verified and the budget approved.</p>
        </div>
      </section>

      <section className="py-10 bg-white border-b border-black/5" data-testid="stage-explainer">
        <div className="dk-container grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {Object.entries(STAGES).map(([k, s], i) => (
            <div key={k} className="p-5 rounded-2xl bg-[#f8f4e8] border border-black/5">
              <div className="font-mono text-[10px] text-[#8a6a1f]">STAGE {i + 1}</div>
              <div className="font-serif text-lg font-bold text-[#0a0d12] mt-1">{s.label}</div>
              <p className="text-xs text-[#0a0d12]/70 mt-2 leading-relaxed">{s.note}</p>
              <div className="text-[10px] uppercase tracking-wider font-bold text-[#0d1724] mt-3">{k === "under_verification" ? "Action: Register interest" : k === "verified_fundraising" ? "Action: Fund this project" : k === "funded_implementation" ? "Action: Follow progress" : "Action: Read completion report"}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="py-16 bg-[#f8f4e8]">
        <div className="dk-container">
          <div className="flex flex-wrap gap-2 mb-8" role="tablist" aria-label="Filter projects by stage" data-testid="stage-tabs">
            {TABS.map(([k, l]) => (
              <button key={k} role="tab" aria-selected={tab === k} onClick={() => setSp(k === "all" ? {} : { stage: k })} className={`px-4 py-2 rounded-full text-xs font-bold border transition-colors ${tab === k ? "bg-[#155e3f] text-[#d6aa43] border-[#0d1724]" : "bg-white text-[#0d1724] border-black/10 hover:border-[#0d1724]"}`} data-testid={`stage-tab-${k}`}>
                {l} <span className="opacity-60">({k === "all" ? projects.length : counts[k] || 0})</span>
              </button>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">{shown.map((p, i) => <ProjectCard key={p.id} project={p} index={i} />)}</div>
          {shown.length === 0 && <p className="text-center text-[#0a0d12]/60" data-testid="projects-empty">No projects at this stage yet.</p>}
        </div>
      </section>
    </div>
  );
}
